import React, { useState, useEffect, useCallback } from "react";
import { getAllUsers } from "../../api/userApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UpdateUser from "./models/UpdateUser";
import ViewUser from "./models/ViewUser";
import { debounce } from "lodash";
import { Search, ChevronDown, ChevronUp, User, Eye, Edit2 } from "lucide-react";

// Skeleton loader component for the table rows
const TableRowSkeleton = () => {
  return Array(8).fill(0).map((_, index) => (
    <tr key={index} className="animate-pulse">
      {Array(13).fill(0).map((_, cellIndex) => (
        <td key={cellIndex} className="px-6 py-4 whitespace-nowrap">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </td>
      ))}
    </tr>
  ));
};

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [sortColumn, setSortColumn] = useState("user_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const limit = 30;

  // Fetch users with pagination, search, and sorting
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getAllUsers({
        page: currentPage,
        limit,
        search: searchTerm,
        sortColumn,
        sortOrder,
      });

      setUsers(data.users);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setTotalUsers(data.totalUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 500),
    []
  );

  // Handle search input
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  // Fetch users when dependencies change
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, sortColumn, sortOrder]);

  // Listen for update events from ViewUser modal
  useEffect(() => {
    const handleUpdateEvent = (event) => {
      const userId = event.detail;
      handleOpenUpdateModal(userId);
    };

    window.addEventListener("openUpdateModal", handleUpdateEvent);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener("openUpdateModal", handleUpdateEvent);
    };
  }, []);

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Default to ascending order for a new column
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Handle opening the update modal
  const handleOpenUpdateModal = (userId) => {
    setSelectedUserId(userId);
    setIsUpdateModalOpen(true);
  };

  // Handle opening the view modal
  const handleOpenViewModal = (userId) => {
    setSelectedUserId(userId);
    setIsViewModalOpen(true);
  };

  // Handle user update
  const handleUserUpdate = (updatedUser) => {
    // Update the user in the users array
    setUsers(
      users.map((user) =>
        user.user_id === updatedUser.user_id ? updatedUser : user
      )
    );
    toast.success("User updated successfully");
  };

  // Get the status badge class
  const getStatusBadgeClass = (status) => {
    return status === 1
      ? "bg-green-100 text-green-800 border border-green-200"
      : "bg-yellow-100 text-yellow-800 border border-yellow-200";
  };

  // Column configuration for the table
  const columns = [
    { key: "user_id", label: "User ID" },
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "phone_number", label: "Phone" },
    { key: "NIC", label: "NIC" },
    { key: "email", label: "Email" },
    { key: "address", label: "Address" },
    { key: "district", label: "District" },
    { key: "city", label: "City" },
    { key: "status", label: "Status" },
    { key: "type", label: "Type" },
    { key: "date", label: "Date" },
  ];

  return (
    <div className="container mx-auto p-3 md:p-6">
      <div className="bg-white rounded-lg  mb-6">
        <div className="flex flex-col md:flex-row items-start gap-6  md:items-center justify-between mb-6 md:mb-8 mt-6 md:mt-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <User className="mr-2 text-black size-7" />
              User Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              View and manage all registered users
            </p>
          </div>
          <div className="mt-2 md:mt-0 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
            <span className="font-medium text-blue-800">Total Users:</span>{" "}
            <span className="text-blue-600 font-bold">{totalUsers}</span>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name, email, phone number, NIC or user ID..."
            value={searchInputValue}
            onChange={handleSearch}
            className="pl-10 p-3 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
          {searchInputValue && isLoading && (
            <div className="absolute inset-y-0 right-3 flex items-center">
              <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
          <table className="min-w-full bg-white divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <span>{column.label}</span>
                      <span className="ml-1">
                        {sortColumn === column.key ? (
                          sortOrder === "asc" ? (
                            <ChevronUp className="h-4 w-4 text-blue-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-blue-500" />
                          )
                        ) : (
                          <div className="h-4 w-4"></div> // Spacer
                        )}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <TableRowSkeleton />
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user.user_id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.first_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.last_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.phone_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.NIC}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.district}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                          user.status
                        )}`}
                      >
                        {user.status === 1 ? "Verified" : "Pending"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {new Date(user.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenViewModal(user.user_id)}
                          className="text-blue-600 hover:text-blue-900 flex items-center transition-colors p-1 rounded hover:bg-blue-50"
                          title="View User Details"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="ml-1">View</span>
                        </button>
                        <button
                          onClick={() => handleOpenUpdateModal(user.user_id)}
                          className="text-emerald-600 hover:text-emerald-900 flex items-center transition-colors p-1 rounded hover:bg-emerald-50"
                          title="Update User"
                        >
                          <Edit2 className="h-4 w-4" />
                          <span className="ml-1">Edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="13"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <User className="h-12 w-12 text-gray-300 mb-2" />
                      <p className="text-lg font-medium">No users found</p>
                      <p className="text-sm text-gray-400">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center mt-6 px-2">
          <div className="text-sm text-gray-700 mb-4 md:mb-0">
            Showing{" "}
            <span className="font-medium">
              {users.length > 0 ? (currentPage - 1) * limit + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * limit, totalUsers)}
            </span>{" "}
            of <span className="font-medium">{totalUsers}</span> users
          </div>
          <div className="flex justify-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1 || isLoading}
              className="px-3 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center px-4 py-2 border rounded-md bg-white">
              <span className="text-sm font-medium text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoading}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages || isLoading}
              className="px-3 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* Update User Modal */}
      <UpdateUser
        userId={selectedUserId}
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdate={handleUserUpdate}
      />

      {/* View User Modal */}
      <ViewUser
        userId={selectedUserId}
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
      />
    </div>
  );
};

export default UserManager;